import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Dayjs } from "dayjs";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { pushGlobalError } from "../../errors/globalError";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { MonthlyExpenseGroup } from "../../types/monthly-expense.type";
import { formatNumber } from "../../utils";
import { todayInBangkok } from "../../utils/date";
import { useLeavePageGuard } from "../../utils/hooks/useLeavePageGuard";
import ConfirmModal from "../ConfirmModal";
import { API_MONTH_FORMAT, MONTH_PARAM } from "../DailyExpense/constants";
import { parseMonthParam } from "../DailyExpense/ExpenseMonthPicker";
import { formatMonthLabel, formatTimestamp } from "../DailyExpense/format";
import SkeletonCard from "../DailyExpense/SkeletonCard";
import StateCard from "../DailyExpense/StateCard";
import AddExtraDialog, { ExtraPreset } from "./AddExtraDialog";
import CloseBanners from "./CloseBanners";
import CloseHeader from "./CloseHeader";
import CloseMonthDialog from "./CloseMonthDialog";
import CloseSummaryCards from "./CloseSummaryCards";
import CloseTable from "./CloseTable";
import CloseToolbar from "./CloseToolbar";
import { ADJUSTED_SX, ESTIMATE_TEXT_SX } from "./closeTableStyles";
import { ColumnGroupKey, DEFAULT_EXPANDED, EMPTY_TEXT } from "./constants";
import {
  CloseCellTarget,
  EstimateSide,
  ExtraInput,
  addExtras,
  closeSummaryOf,
  commitCell,
  copyPreviousItems,
  removeExtra,
  revertAllEstimates,
  revertEstimate,
  roundUpAll,
  routeDaysOf,
} from "./monthlyExpenseDraft";
import { closeLayoutOf } from "./monthlyExpenseLayout";
import { AmountView, weightedAverageOf } from "./monthlyExpenseMath";
import { reviewMonthPathOf, warningLinksOf } from "./monthlyExpenseWarnings";
import { useMonthlyExpenseClose } from "./useMonthlyExpenseClose";

type CloseDialog =
  | { kind: "ADD_EXTRA"; preset: ExtraPreset; sequence: number }
  | { kind: "CLOSE_MONTH"; sequence: number }
  | { kind: "COPY_PREVIOUS" }
  | { kind: "REVERT_ALL" };

const NEW_EXTRA_PRESET: ExtraPreset = { branchCodes: [], group: "ADMIN" };

/** The month usually closed: the last one that has ended (Bangkok). */
const defaultMonthOf = () =>
  todayInBangkok().subtract(1, "month").startOf("month");

/**
 * S4 ปิดเดือน / ประมาณการ (head office): every van branch's month M as an Excel-like sheet — the
 * divisor, the approved daily sums (overwritable), the 16 items, the extras — and the Sales /
 * Manager per route per day that become the M+1 estimate, adjustable by CEIL or by typing. บันทึกร่าง
 * any number of times; ส่งและปิดเดือน once, after a summary and a tick. A closed month is read-only.
 */
export default function MonthlyExpenseCloseScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get(MONTH_PARAM);
  const month = useMemo(
    () => parseMonthParam(monthParam) ?? defaultMonthOf(),
    [monthParam],
  );
  const yearMonth = month.format(API_MONTH_FORMAT);
  const close = useMonthlyExpenseClose(yearMonth);
  const { rows, draft, figures, isLive, isEditable } = close;
  const [view, setView] = useState<AmountView>("MONTH");
  const [expanded, setExpanded] = useState(DEFAULT_EXPANDED);
  const [dialog, setDialog] = useState<CloseDialog | null>(null);
  const [pendingMonth, setPendingMonth] = useState<Dayjs | null>(null);
  const sequence = useRef(0);
  const isDirty = close.changedCount > 0;
  const leaveGuard = useLeavePageGuard(isDirty);

  const monthTitle = formatMonthLabel(yearMonth);
  const estimateTitle = `ประมาณการ ${formatMonthLabel(close.month?.estimate_month ?? month.add(1, "month").format(API_MONTH_FORMAT))}`;
  const layout = useMemo(
    () => closeLayoutOf(rows, close.categories ?? []),
    [rows, close.categories],
  );
  const summary = useMemo(
    () => (draft ? closeSummaryOf(rows, draft, figures) : null),
    [rows, draft, figures],
  );
  const averages = useMemo(() => {
    const averageOf = (side: EstimateSide) =>
      weightedAverageOf(
        figures.map((figure, index) => ({
          value: figure.estimate[side].effective,
          routeDays: routeDaysOf(rows[index]),
        })),
      );
    return { SALES: averageOf("SALES"), MANAGER: averageOf("MANAGER") };
  }, [rows, figures]);
  const warningLinks = useMemo(
    () => warningLinksOf(rows, yearMonth, todayInBangkok()),
    [rows, yearMonth],
  );
  const warnedBranchCount = new Set(
    warningLinks.map((warning) => warning.branchCode),
  ).size;
  const isClosed = close.month ? close.month.status === "CLOSED" : null;
  const closedText =
    close.month?.status === "CLOSED"
      ? `ปิดเดือน${monthTitle} แล้ว เมื่อ ${formatTimestamp(close.month.closed_at)} โดย ${close.month.closed_by_name ?? EMPTY_TEXT} · แก้ไขหรือเปิดเดือนกลับไม่ได้ · เอกสารรายวันของเดือนนี้ถูกล็อก`
      : null;
  const hasPreviousItems = rows.some((row) => row.previous_items.length > 0);

  const applyMonth = (value: Dayjs) => {
    setDialog(null);
    setSearchParams(
      { [MONTH_PARAM]: value.format(API_MONTH_FORMAT) },
      { replace: true },
    );
  };
  const handleMonthChange = (value: Dayjs | null) => {
    // A half-typed month is not a month yet; the screen keeps the last whole one.
    if (!value?.isValid() || value.format(API_MONTH_FORMAT) === yearMonth)
      return;
    if (isDirty) setPendingMonth(value);
    else applyMonth(value);
  };
  const stayOnMonth = () => setPendingMonth(null);
  const leaveMonth = () => {
    if (pendingMonth) applyMonth(pendingMonth);
    setPendingMonth(null);
  };

  const handleToggleGroup = (groupKey: ColumnGroupKey) =>
    setExpanded((previous) => ({
      ...previous,
      [groupKey]: !previous[groupKey],
    }));
  const handleCommit = (target: CloseCellTarget, text: string) =>
    close.updateDraft((current) => commitCell(current, target, text));
  const handleRevertEstimate = (branchCode: string, side: EstimateSide) =>
    close.updateDraft((current) => revertEstimate(current, branchCode, side));
  const handleRemoveExtra = (branchCode: string, key: string) =>
    close.updateDraft((current) => removeExtra(current, branchCode, key));
  const openExtraDialog = (preset: ExtraPreset) => {
    sequence.current += 1;
    setDialog({ kind: "ADD_EXTRA", preset, sequence: sequence.current });
  };
  const handleAddExtraFor = (branchCode: string, group: MonthlyExpenseGroup) =>
    openExtraDialog({ branchCodes: [branchCode], group });
  const handleOpenAddExtra = () => openExtraDialog(NEW_EXTRA_PRESET);
  const closeDialog = () => setDialog(null);

  const handleSubmitExtra = (input: ExtraInput) => {
    const extraSequence = sequence.current;
    close.updateDraft((current) => addExtras(current, input, extraSequence));
    setDialog(null);
    pushGlobalSuccess(
      `เพิ่มค่าใช้จ่ายแล้ว · ${input.description.trim()} · ${input.branchCodes.length} สาขา · ${formatNumber(input.amount)} บาทต่อสาขา`,
    );
  };
  const handleRoundUp = () => {
    if (!draft) return;
    const result = roundUpAll(draft, rows, isLive);
    close.updateDraft(() => result.draft);
    pushGlobalSuccess(
      `ปัดขึ้นเป็นจำนวนเต็มแล้ว · ปรับ ${result.changedCount} ช่อง`,
    );
  };
  const askCopyPrevious = () => setDialog({ kind: "COPY_PREVIOUS" });
  const handleCopyPrevious = () => {
    if (!draft) return;
    const result = copyPreviousItems(draft, rows);
    close.updateDraft(() => result.draft);
    setDialog(null);
    pushGlobalSuccess(
      `คัดลอกยอดเดือนก่อนแล้ว · แทนที่ 16 รายการของ ${result.copiedCount} สาขา`,
    );
  };
  const askRevertAll = () => setDialog({ kind: "REVERT_ALL" });
  const handleRevertAll = () => {
    close.updateDraft(revertAllEstimates);
    setDialog(null);
  };

  /** A refused figure is still in its cell: nothing is sent until it is fixed. */
  const hasNoErrors = () => {
    if (close.errorCount === 0) return true;
    pushGlobalError(
      `ตรวจตัวเลขอีกครั้ง · มี ${close.errorCount} ช่องที่ไม่ถูกต้อง`,
    );
    return false;
  };
  const handleSave = async () => {
    if (!hasNoErrors()) return;
    if (await close.save())
      pushGlobalSuccess("บันทึกร่างแล้ว · ยังไม่ส่งให้สาขา");
  };
  const handleOpenClose = () => {
    if (!hasNoErrors()) return;
    sequence.current += 1;
    setDialog({ kind: "CLOSE_MONTH", sequence: sequence.current });
  };
  const handleConfirmClose = async () => {
    const isClosedNow = await close.close();
    setDialog(null);
    if (isClosedNow) {
      pushGlobalSuccess(
        `ปิดเดือน${monthTitle} แล้ว · ส่ง${estimateTitle}ให้ ${rows.length} สาขาแล้ว`,
      );
    }
  };

  const isEmpty = close.month !== null && rows.length === 0;
  const isReady =
    Boolean(close.month && draft && close.categories && summary) && !isEmpty;

  return (
    <Stack sx={{ py: 1 }} gap={2}>
      <CloseHeader
        month={month}
        estimateText={close.month ? estimateTitle : null}
        isClosed={isClosed}
        canEdit={isEditable}
        canClose={Boolean(close.month?.actions.can_close)}
        hasChanges={isDirty}
        pendingWrite={close.pendingWrite}
        onMonthChange={handleMonthChange}
        onSave={handleSave}
        onClose={handleOpenClose}
      />

      {close.isLoading && <SkeletonCard rowCount={12} />}
      {close.isFailed && (
        <StateCard
          title="โหลดข้อมูลปิดเดือนไม่สำเร็จ"
          text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ค่าที่บันทึกร่างไว้ยังอยู่"
          onRetry={close.reload}
        />
      )}
      {isEmpty && (
        <StateCard
          title="ยังไม่มีข้อมูลของเดือนนี้"
          text="ยังไม่มีสาขาวิ่งในเดือนที่เลือก ระบบจะรวมยอดให้เมื่อมีเอกสารรายวันที่อนุมัติแล้ว"
        />
      )}

      {isReady && draft && summary && (
        <>
          <CloseBanners
            closedText={closedText}
            warningLinks={warningLinks}
            warnedBranchCount={warnedBranchCount}
            reviewPath={reviewMonthPathOf(yearMonth, todayInBangkok())}
          />
          <CloseSummaryCards
            monthTitle={monthTitle}
            estimateTitle={estimateTitle}
            monthTotal={summary.monthTotal}
            averageSales={averages.SALES}
            averageManager={averages.MANAGER}
            adjustedCount={summary.adjustedCount}
            monthlyImpact={summary.monthlyImpact}
          />
          <Paper>
            <CloseToolbar
              view={view}
              isEditable={isEditable}
              hasPreviousItems={hasPreviousItems}
              hasAdjustments={summary.adjustedCount > 0}
              onViewChange={setView}
              onAddExtra={handleOpenAddExtra}
              onCopyPrevious={askCopyPrevious}
              onRoundUp={handleRoundUp}
              onRevertAll={askRevertAll}
            />
            <CloseTable
              rows={rows}
              draft={draft}
              figures={figures}
              layout={layout}
              expanded={expanded}
              view={view}
              isEditable={isEditable}
              monthTitle={monthTitle}
              estimateTitle={estimateTitle}
              onToggleGroup={handleToggleGroup}
              onCommit={handleCommit}
              onRevertEstimate={handleRevertEstimate}
              onAddExtra={handleAddExtraFor}
              onRemoveExtra={handleRemoveExtra}
            />
            <Stack direction="row" gap={2} flexWrap="wrap" mt={1.25}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={ADJUSTED_SX}
                px={0.75}
                borderRadius={1}
              >
                ปรับหรือแก้ทับแล้ว
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <Typography
                  component="i"
                  variant="caption"
                  sx={ESTIMATE_TEXT_SX}
                >
                  ตัวเอียงสีม่วง
                </Typography>{" "}
                = ยอด{estimateTitle}{" "}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ยอดที่ระบบคำนวณ = ยอดจริงของเดือน{monthTitle}{" "}
              </Typography>
            </Stack>
          </Paper>

          {dialog?.kind === "ADD_EXTRA" && (
            <AddExtraDialog
              key={dialog.sequence}
              open
              branches={rows}
              preset={dialog.preset}
              monthTitle={monthTitle}
              estimateTitle={estimateTitle}
              onClose={closeDialog}
              onSubmit={handleSubmitExtra}
            />
          )}
          {dialog?.kind === "CLOSE_MONTH" && (
            <CloseMonthDialog
              key={dialog.sequence}
              open
              summary={summary}
              monthTitle={monthTitle}
              estimateTitle={estimateTitle}
              averageSales={averages.SALES}
              averageManager={averages.MANAGER}
              isClosing={close.pendingWrite === "CLOSE"}
              onClose={closeDialog}
              onConfirm={handleConfirmClose}
            />
          )}
          <ConfirmModal
            open={dialog?.kind === "COPY_PREVIOUS"}
            title="คัดลอกยอดเดือนก่อน"
            description={`แทนที่ 16 รายการที่ HO กรอก (ฝ่ายขาย · เงินเดือน · ฝ่ายบริหาร) ของทุกสาขาด้วยยอดเดือน${formatMonthLabel(month.subtract(1, "month").format(API_MONTH_FORMAT))} · กลุ่มจากรายวันและค่าใช้จ่ายเพิ่มเติมไม่เปลี่ยน · สาขาที่เดือนก่อนไม่มียอดคงค่าเดิม`}
            confirmLabel="คัดลอก"
            onClose={closeDialog}
            onConfirm={handleCopyPrevious}
          />
          <ConfirmModal
            open={dialog?.kind === "REVERT_ALL"}
            title="คืนค่าระบบทั้งหมด"
            description="ยอดประมาณการทุกช่องจะกลับเป็นค่าที่ระบบคำนวณ (ค่าใช้จ่ายเพิ่มเติมยังอยู่)"
            confirmLabel="คืนค่าระบบ"
            onClose={closeDialog}
            onConfirm={handleRevertAll}
          />
        </>
      )}

      <ConfirmModal
        open={leaveGuard.isAskingToLeave || pendingMonth !== null}
        title="ออกโดยไม่บันทึก?"
        description="ค่าที่แก้ในตารางยังไม่ได้บันทึกร่าง ถ้าออกตอนนี้ค่าที่แก้จะหายไป"
        cancelLabel="อยู่ต่อ"
        confirmLabel="ออกโดยไม่บันทึก"
        confirmColor="error"
        onClose={pendingMonth ? stayOnMonth : leaveGuard.stay}
        onConfirm={pendingMonth ? leaveMonth : leaveGuard.leave}
      />
    </Stack>
  );
}
